import { UI_THEME } from "@bale-bot/config";
import LogoutIcon from "@mui/icons-material/Logout";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import {
  AppBar,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { hasPermission, useAuthStore } from "../store/auth.store";
import { type ThemeMode, useThemeContext } from "@/theme/ThemeContext";
import { NAV_ITEMS } from "./NavItems";

export default function Layout() {
  const logout = useAuthStore((state) => state.logout);
  const user = useAuthStore((state) => state.user);
  const { pathname } = useLocation();
  const { mode, setMode, actualMode } = useThemeContext();
  const navItems = NAV_ITEMS.filter(
    (item) => !item.permission || hasPermission(user, item.permission),
  );

  const handleThemeToggle = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    const newMode = mode === "dark" ? "light" : "dark";
    setMode(newMode as ThemeMode, event);
  };

  const orbVars =
    actualMode === "dark"
      ? {
          ["--orb-a" as string]: alpha(UI_THEME.primaryColor, 0.55),
          ["--orb-b" as string]: alpha("#a78bfa", 0.45),
        }
      : {
          ["--orb-a" as string]: alpha(UI_THEME.primaryColor, 0.45),
          ["--orb-b" as string]: alpha("#c4b5fd", 0.5),
        };

  return (
    <Box className="app-shell" sx={{ display: "flex", minHeight: "100vh", ...orbVars }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar sx={{ gap: 2, px: { xs: 2, md: 3 } }}>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontWeight: 650,
              letterSpacing: "-0.02em",
            }}
          >
            Bale Bot Admin
          </Typography>
          {user && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: { xs: "none", sm: "block" },
                px: 1.5,
                py: 0.5,
                borderRadius: 999,
                border: (t) => `1px solid ${t.palette.divider}`,
                bgcolor: (t) =>
                  alpha(t.palette.background.paper, 0.5),
              }}
            >
              {user.username} · {user.role}
            </Typography>
          )}
          <IconButton
            color="inherit"
            onClick={handleThemeToggle}
            size="small"
            sx={{
              border: (t) => `1px solid ${t.palette.divider}`,
              bgcolor: (t) => alpha(t.palette.background.paper, 0.4),
            }}
          >
            {mode === "light" ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
          <Button
            size="small"
            startIcon={<LogoutIcon />}
            onClick={() => void logout()}
            variant="outlined"
          >
            Sign out
          </Button>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: UI_THEME.drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: UI_THEME.drawerWidth,
            boxSizing: "border-box",
          },
        }}
      >
        <Toolbar />
        <Box sx={{ px: 1.5, py: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ px: 1.5, mb: 1, display: "block", letterSpacing: "0.06em" }}
          >
            MENU
          </Typography>
          <List disablePadding>
            {navItems.map((item) => (
              <ListItemButton
                key={item.to}
                component={NavLink}
                to={item.to}
                selected={pathname === item.to}
                sx={{
                  position: "relative",
                  "&.Mui-selected::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: "20%",
                    bottom: "20%",
                    width: 3,
                    borderRadius: 2,
                    bgcolor: "primary.main",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 36,
                    color: pathname === item.to ? "primary.main" : "inherit",
                    transition: "color 0.2s ease",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: 0,
          px: { xs: 2, md: 3.5 },
          py: { xs: 2.5, md: 3.5 },
        }}
      >
        <Toolbar />
        <Box className="page-enter" key={pathname}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
